import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { NotificationTextMessage } from '@/core/enums/notification';
import {
  REVISION_TYPES,
  type ApprovalState,
  type DocumentVersionRow,
  type FinalisationSnapshot,
  type RevisionCase,
} from '@/core/models/approval.models';
import type { PeriodRow } from '@/core/models/practice.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { resolveDefaultPeriodId } from '@/core/periodDefault';
import { approvalService } from '@/core/services/approval';
import { practiceService } from '@/core/services/practice';
import { Button } from '@/kit/Button';
import { DataTable } from '@/kit/DataTable';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { PageHeader } from '@/kit/PageHeader';
import { Select } from '@/kit/Select';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { AccountingPeriodSelect, PERIODS_ROUTE, currentPeriod, displayDate, useClientGuard } from './shared';
import './TaxreturnRevisions.scss';

/** ap5-a6 (item 113): one pickable revised-accounts document. */
interface RevisedDocumentOption {
  id: string;
  label: string;
}

function documentLabel(document: DocumentVersionRow): string {
  const types = ['Draft accounts', 'Final accounts', 'iXBRL', 'Evidence', 'Acknowledgement'];
  const statuses = ['Requested', 'Generating', 'Generated', 'Approved', 'Final', 'Failed'];
  const type = types[document.type] ?? 'Document';
  const status = statuses[document.status] ?? 'Unknown';
  return `v${document.versionNo} - ${type} (${status})`;
}

function revisionTypeLabel(value: number | null): string {
  if (value === null || value === undefined) {
    return 'Not set';
  }
  return REVISION_TYPES.find((t) => t.value === value)?.label ?? '';
}

function shortHash(hash: string | null): string {
  return hash ? `${hash.substring(0, 12)}...` : '-';
}

/**
 * Revised-accounts lineage of a finalised period: revision history and finalisation snapshots; open a revision,
 * finalise the open one against a generated accounts version, or abandon it. (Its left menu is the Settings menu.)
 */
export function TaxreturnRevisions() {
  const navigate = useNavigate();
  const client = useClientGuard();

  const [periods, setPeriods] = useState<PeriodRow[]>([]);
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);

  const [state, setState] = useState<ApprovalState | null>(null);
  const [revisions, setRevisions] = useState<RevisionCase[]>([]);
  const [snapshots, setSnapshots] = useState<FinalisationSnapshot[]>([]);

  /** New revision being opened. */
  const [newReason, setNewReason] = useState('');
  const [newRevisionType, setNewRevisionType] = useState<number | null>(null);

  /** Document version used to finalise the open revision. */
  const [revisedDocumentVersionId, setRevisedDocumentVersionId] = useState<string | null>('');
  /** ap5-a6 (item 113): the period's generated accounts, so the approved revised version can be picked. */
  const [documents, setDocuments] = useState<DocumentVersionRow[]>([]);
  const [isDocumentsLoaded, setIsDocumentsLoaded] = useState(false);

  const load = (periodId: string | null) => {
    if (!periodId) {
      return;
    }
    approvalService.getState(periodId).then(setState, (error) => notifyError(error));
    approvalService.getRevisions(periodId).then(
      (result) => setRevisions(result ?? []),
      (error) => notifyError(error),
    );
    // ap5-a6 (item 113): list the period's generated accounts so the finalise step can offer them.
    setIsDocumentsLoaded(false);
    approvalService.getPeriodDocuments(periodId).then(
      (result) => {
        setDocuments(result ?? []);
        setIsDocumentsLoaded(true);
      },
      // A period with no generated accounts is normal; do not shout about it.
      () => {
        setDocuments([]);
        setIsDocumentsLoaded(true);
      },
    );
    approvalService.getSnapshots(periodId).then(
      (result) => setSnapshots(result ?? []),
      (error) => notifyError(error),
    );
  };

  useEffect(() => {
    if (!client) {
      return;
    }
    practiceService.getClientPeriods(client.id).then(
      async (response) => {
        const list = response.periods ?? [];
        setPeriods(list);
        const id = (await resolveDefaultPeriodId(client.id, list)) ?? currentPeriod(list)?.id ?? null;
        setSelectedPeriodId(id);
        if (id) {
          load(id);
        } else {
          notifyError('Add an accounting period for this client first.');
        }
      },
      (error) => notifyError(error),
    );
  }, [client]);

  const onPeriodChange = (periodId: string) => {
    setSelectedPeriodId(periodId);
    load(periodId);
  };

  /** ap5-a6 (item 113): only generated (2), approved (3) or final (4) accounts can be finalised against. */
  const finalisableDocuments: RevisedDocumentOption[] = documents
    .filter((d) => d.status === 2 || d.status === 3 || d.status === 4)
    .map((d) => ({ id: d.documentVersionId, label: documentLabel(d) }));

  /** A revision can only be opened from finalised accounts. */
  const canRevise = !!state && (state.periodStatus === 5 || state.periodStatus === 6);

  const openRevision = revisions.find((r) => r.status === 'Open' || r.status === 'ReadyForApproval');

  /** A create, finalise or abandon request is running (stops a second click). */
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const begin = () => {
    if (savingRef.current) return false;
    savingRef.current = true;
    setSaving(true);
    return true;
  };
  const finish = () => {
    savingRef.current = false;
    setSaving(false);
  };

  const createRevision = () => {
    const periodId = selectedPeriodId;
    if (!periodId) {
      notifyError('Select an accounting period first.');
      return;
    }
    // ap5-a6 (item 112): the type must be chosen, it is not defaulted for the user.
    if (newRevisionType === null || newRevisionType === undefined) {
      notifyError('Choose how the accounts are revised.');
      return;
    }
    if (!begin()) {
      return;
    }
    approvalService
      .createRevision(periodId, { reason: newReason || null, revisionType: newRevisionType })
      .then(
        () => {
          setNewReason('');
          notifySuccess('Revision opened. The original final accounts remain unchanged and still reproducible.');
          load(periodId);
        },
        (error) => notifyError(error),
      )
      .finally(finish);
  };

  const finaliseRevision = (revision: RevisionCase) => {
    if (!revisedDocumentVersionId) {
      notifyError('Enter the approved revised document version.');
      return;
    }
    if (!begin()) {
      return;
    }
    approvalService
      .finaliseRevision(revision.revisionCaseId, { documentVersionId: revisedDocumentVersionId, comment: null })
      .then(
        () => {
          setRevisedDocumentVersionId('');
          notifySuccess('Revised accounts finalised. The original artefacts were retained.');
          load(selectedPeriodId);
        },
        (error) => notifyError(error),
      )
      .finally(finish);
  };

  const abandonRevision = (revision: RevisionCase) => {
    if (!begin()) {
      return;
    }
    approvalService
      .abandonRevision(revision.revisionCaseId, 'Abandoned by the practice.')
      .then(
        () => {
          notifySuccess(NotificationTextMessage.successMessage);
          load(selectedPeriodId);
        },
        (error) => notifyError(error),
      )
      .finally(finish);
  };

  // Angular bound a `[title]` property, which stringifies a null hash to "null".
  const hashCell = (hash: string | null) => (
    <span className="hash" title={String(hash)}>
      {shortHash(hash)}
    </span>
  );

  return (
    <SideMenuPage menu="settings" contentClassName="taxreturn-revisions">
      <div className="sidenav-details">
        {/* Design review 2 Nov 2026: the same sticky page header as the other settings pages. */}
        <PageHeader title="Revised Accounts Lineage">
          <Button className="close-btn" aria-label="Close" onClick={() => void navigate(PERIODS_ROUTE)}>
            <Icon name="close" />
          </Button>
        </PageHeader>

        <div className="sidenav-body">
          <div className="form-field-title pt-1 pb-1 d-flex align-items-center">
            <span className="pr-1">Accounting period</span>
            <AccountingPeriodSelect periods={periods} value={selectedPeriodId} onChange={onPeriodChange} />
          </div>

          {state && !canRevise && (
            <div className="alert-message alert-info">
              <Icon name="info" outlined />
              {/* task-113: the message named the blocker but not where to clear it, and finalising
                  lives on another screen, so there was nothing to act on from here. */}
              <div>
                Only finalised accounts can be revised. Finalise this period first, on the Approval screen.
                <Button className="link-button" onClick={() => void navigate('/home/taxreturns/tasks-approval')}>
                  Open Approval
                </Button>
              </div>
            </div>
          )}

          {/* Open a revision */}
          {canRevise && !openRevision && (
            <div className="form-field-title pt-1 pb-1">
              <div className="d-flex align-items-center flex-wrap">
                <Select<(typeof REVISION_TYPES)[number]>
                  className="w-250"
                  items={REVISION_TYPES}
                  bindValue="value"
                  bindLabel="label"
                  placeholder="How are the accounts revised?"
                  value={newRevisionType}
                  onChange={(value) => setNewRevisionType((value as number | null) ?? null)}
                />
                <InputField
                  fieldClassName="wp-100 ml-1"
                  placeholder="Reason for the revision"
                  maxLength={500}
                  value={newReason}
                  onChange={(event) => setNewReason(event.target.value)}
                />
                <Button className="action-button primary-button border-radius-6 ml-1" busy={saving} onClick={createRevision}>
                  <Icon name="add" />
                  <span>Create Revision</span>
                </Button>
              </div>
              <div className="text-muted pt-1">
                The original final accounts stay immutable: the revision is generated, approved and signed separately,
                and the original artefacts, acknowledgements and hashes are kept.
              </div>
            </div>
          )}

          {/* Finalise the revision in progress */}
          {openRevision && (
            <div className="revision-open">
              <div className="d-flex align-items-center justify-space-between">
                <h3 className="mb-0">Revision {openRevision.revisionNo} in progress</h3>
                <span className="status-chip">{openRevision.status}</span>
              </div>
              <div className="text-muted pt-1">
                Revised as: {revisionTypeLabel(openRevision.revisionType)} &middot; {openRevision.signatoryCount}{' '}
                signature(s) recorded on this revision.
              </div>
              <div className="alert-message alert-warning">
                <Icon name="edit_note" outlined />
                <div>
                  The revision reason, amendments and dated supplementary note are entered on the Revised Accounts
                  screen. Finalising is blocked until they are complete.
                  <Button className="link-button" onClick={() => void navigate('/home/taxreturns/revised-accounts')}>
                    Open Revised Accounts
                  </Button>
                </div>
              </div>
              {/* ap5-a6 (item 113): this used to be a free-text box wanting a document version GUID. */}
              <div className="d-flex align-items-center pt-1">
                <Select<RevisedDocumentOption>
                  className="w-350"
                  items={finalisableDocuments}
                  bindValue="id"
                  bindLabel="label"
                  placeholder="Approved revised accounts"
                  value={revisedDocumentVersionId}
                  onChange={(value) => setRevisedDocumentVersionId((value as string | null) ?? null)}
                />
                <Button
                  className="action-button primary-button border-radius-6 ml-1"
                  busy={saving}
                  onClick={() => finaliseRevision(openRevision)}
                >
                  <span>Finalise Revision</span>
                </Button>
                <Button
                  className="action-button secondary-button border-radius-6 ml-1"
                  busy={saving}
                  onClick={() => abandonRevision(openRevision)}
                >
                  <span>Abandon</span>
                </Button>
              </div>
              {isDocumentsLoaded && finalisableDocuments.length === 0 && (
                <div className="text-muted pt-1">
                  No generated accounts are available to finalise against yet. Generate the revised accounts first; only
                  generated, approved or final versions can be used here.
                </div>
              )}
            </div>
          )}

          {/* Revision lineage */}
          <h3 className="pt-2">Revision history</h3>
          <div className="mat-table-wrapper">
            <DataTable
              rows={revisions}
              rowKey={(r) => r.revisionCaseId}
              columns={[
                { id: 'no', header: 'No', headerClassName: 'w-60', cell: (r) => r.revisionNo },
                { id: 'type', header: 'Revised as', cell: (r) => revisionTypeLabel(r.revisionType) },
                {
                  id: 'status',
                  header: 'Status',
                  headerClassName: 'w-120',
                  cell: (r) => <span className="status-chip">{r.status}</span>,
                },
                { id: 'original', header: 'Original hash', cell: (r) => hashCell(r.originalContentHash) },
                { id: 'revised', header: 'Revised hash', cell: (r) => hashCell(r.revisedContentHash) },
                { id: 'signed', header: 'Signed', headerClassName: 'w-80', cell: (r) => r.signatoryCount },
                {
                  id: 'created',
                  header: 'Created',
                  headerClassName: 'w-120',
                  cell: (r) => displayDate(r.createdAtUtc),
                },
                {
                  id: 'action',
                  header: 'Finalised',
                  headerClassName: 'w-120',
                  cell: (r) => displayDate(r.finalisedAtUtc) || '-',
                },
              ]}
            />
            {!revisions.length && <div className="text-muted pt-1">These accounts have not been revised.</div>}
          </div>

          {/* Immutable finalisation snapshots */}
          <h3 className="pt-2">Finalisation snapshots</h3>
          <div className="text-muted">
            Each snapshot pins the artefact hash and the regulatory, template and taxonomy versions it was produced
            from, so a historical final stays reproducible.
          </div>
          <div className="mat-table-wrapper">
            <DataTable
              rows={snapshots}
              rowKey={(s) => s.finalisationSnapshotId}
              columns={[
                { id: 'version', header: 'Version', headerClassName: 'w-80', cell: (s) => s.versionNo },
                { id: 'hash', header: 'Content hash', cell: (s) => hashCell(s.contentHash) },
                { id: 'data', header: 'Data version', headerClassName: 'w-100', cell: (s) => s.dataVersion },
                {
                  id: 'pack',
                  header: 'Regulatory pack',
                  cell: (s) => (
                    <span className="hash" title={String(s.regulatoryPackVersionId)}>
                      {shortHash(s.regulatoryPackVersionId)}
                    </span>
                  ),
                },
                {
                  id: 'created',
                  header: 'Created',
                  headerClassName: 'w-120',
                  cell: (s) => displayDate(s.createdAtUtc),
                },
              ]}
            />
            {!snapshots.length && <div className="text-muted pt-1">No finalisation snapshots yet.</div>}
          </div>
        </div>
      </div>
    </SideMenuPage>
  );
}
